import {
  StockReconciliationCounts,
  StockReconciliationHistory,
  StockReconciliationHistoryItem,
  StockReconciliationQueue,
  StockReconciliationRunStatus,
  StockReconciliationReconstruction,
  StockReconciliationResult,
  StockReconciliationResultType,
  StockReconciliationRow,
  StockReconciliationRowPage,
  StockReconciliationStatus,
  StockSourceRow,
} from '@/types/StockReconciliation';

type Loose = Record<string, unknown>;

const asRecord = (value: unknown): Loose | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Loose)
    : undefined;

const pick = <T>(value: Loose, camel: string, pascal: string) =>
  (value[camel] ?? value[pascal]) as T;

const hasResultShape = (value?: Loose) =>
  Boolean(
    value &&
      (value.status ||
        value.Status ||
        value.rows ||
        value.Rows ||
        value.counts ||
        value.Counts ||
        value.vinColumns ||
        value.VinColumns),
  );

const asNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const asString = (value: unknown) =>
  value == null ? undefined : String(value);

const asStatus = (value: unknown): StockReconciliationStatus | undefined => {
  const status = String(value ?? '').trim();
  if (
    status === 'completed' ||
    status === 'needsVinColumn' ||
    status === 'invalidFile' ||
    status === 'noSnapshot' ||
    status === 'unauthorized'
  ) {
    return status;
  }
  return undefined;
};

const asResultType = (value: unknown): StockReconciliationResultType => {
  if (value === 'CustomerOnly' || value === 'OneViewOnly') return value;
  return 'Matched';
};

const normalizeRow = (value: unknown): StockReconciliationRow => {
  const row = asRecord(value) ?? {};
  const source = asRecord(pick(row, 'source', 'Source')) ?? {};
  return {
    vin: String(pick(row, 'vin', 'Vin') ?? ''),
    result: asResultType(pick(row, 'result', 'Result')),
    source: source as StockSourceRow,
    oneViewMake: asString(pick(row, 'oneViewMake', 'OneViewMake')),
    oneViewModel: asString(pick(row, 'oneViewModel', 'OneViewModel')),
    oneViewYear: asString(pick(row, 'oneViewYear', 'OneViewYear')),
    oneViewLocation: asString(pick(row, 'oneViewLocation', 'OneViewLocation')),
    oneViewArrived: asString(pick(row, 'oneViewArrived', 'OneViewArrived')),
    oneViewStatus: asString(pick(row, 'oneViewStatus', 'OneViewStatus')),
    dupedInSource: Boolean(pick(row, 'dupedInSource', 'DupedInSource')),
    dupedInOneView: Boolean(pick(row, 'dupedInOneView', 'DupedInOneView')),
  };
};

const normalizeCounts = (value: unknown): StockReconciliationCounts => ({
  customerReport: asNumber(
    pick(asRecord(value) ?? {}, 'customerReport', 'CustomerReport'),
  ),
  oneViewStock: asNumber(
    pick(asRecord(value) ?? {}, 'oneViewStock', 'OneViewStock'),
  ),
  matched: asNumber(pick(asRecord(value) ?? {}, 'matched', 'Matched')),
  matchRate: asNumber(pick(asRecord(value) ?? {}, 'matchRate', 'MatchRate')),
  customerOnly: asNumber(
    pick(asRecord(value) ?? {}, 'customerOnly', 'CustomerOnly'),
  ),
  oneViewOnly: asNumber(
    pick(asRecord(value) ?? {}, 'oneViewOnly', 'OneViewOnly'),
  ),
  dupedInSource: asNumber(
    pick(asRecord(value) ?? {}, 'dupedInSource', 'DupedInSource'),
  ),
  dupedInOneView: asNumber(
    pick(asRecord(value) ?? {}, 'dupedInOneView', 'DupedInOneView'),
  ),
});

const normalizeReconstruction = (
  value: unknown,
): StockReconciliationReconstruction => {
  const reconstruction = asRecord(value) ?? {};
  return {
    snapshotUsed: asString(
      pick(reconstruction, 'snapshotUsed', 'SnapshotUsed'),
    ),
    snapshotStock: asNumber(
      pick(reconstruction, 'snapshotStock', 'SnapshotStock'),
    ),
    arrivals: asNumber(pick(reconstruction, 'arrivals', 'Arrivals')),
    dispatches: asNumber(pick(reconstruction, 'dispatches', 'Dispatches')),
    reconstructedStock: asNumber(
      pick(reconstruction, 'reconstructedStock', 'ReconstructedStock'),
    ),
  };
};

export const unwrapStockReconciliationResult = (
  value: unknown,
): StockReconciliationResult | undefined => {
  const root = asRecord(value);
  if (!root || root.errorResponse) return undefined;

  const nested = asRecord(root.data);
  const payload = hasResultShape(root)
    ? root
    : hasResultShape(nested)
      ? nested
      : (nested ?? root);

  if (!payload || !hasResultShape(payload)) return undefined;

  const rowsValue = pick<unknown>(payload, 'rows', 'Rows');
  const status =
    asStatus(pick(payload, 'status', 'Status')) ??
    (Array.isArray(rowsValue) ? 'completed' : undefined);

  return {
    id: asNumber(pick(payload, 'id', 'Id')) || undefined,
    status: status ?? 'invalidFile',
    message: asString(pick(payload, 'message', 'Message')),
    vinColumns: (pick(payload, 'vinColumns', 'VinColumns') as string[]) ?? [],
    customerId: asNumber(pick(payload, 'customerId', 'CustomerId')),
    customerName: String(pick(payload, 'customerName', 'CustomerName') ?? ''),
    locationId: asNumber(pick(payload, 'locationId', 'LocationId')),
    locationName: String(pick(payload, 'locationName', 'LocationName') ?? ''),
    reconciliationAt: String(
      pick(payload, 'reconciliationAt', 'ReconciliationAt') ?? '',
    ),
    fileName: asString(pick(payload, 'fileName', 'FileName')),
    sourceColumns:
      (pick(payload, 'sourceColumns', 'SourceColumns') as string[]) ?? [],
    reconstruction: normalizeReconstruction(
      pick(payload, 'reconstruction', 'Reconstruction'),
    ),
    counts: normalizeCounts(pick(payload, 'counts', 'Counts')),
    notes: (pick(payload, 'notes', 'Notes') as string[]) ?? [],
    rows: Array.isArray(rowsValue) ? rowsValue.map(normalizeRow) : [],
  };
};

const unwrapPayload = (value: unknown): Loose | undefined => {
  const root = asRecord(value);
  if (!root || root.errorResponse) return undefined;
  const nested = asRecord(root.data);
  return nested ?? root;
};

const normalizeHistoryItem = (
  value: unknown,
): StockReconciliationHistoryItem => {
  const item = asRecord(value) ?? {};
  return {
    id: asNumber(pick(item, 'id', 'Id')),
    customerId: asNumber(pick(item, 'customerId', 'CustomerId')),
    customerName: String(pick(item, 'customerName', 'CustomerName') ?? ''),
    locationId: asNumber(pick(item, 'locationId', 'LocationId')),
    locationName: String(pick(item, 'locationName', 'LocationName') ?? ''),
    reconciliationAt: String(
      pick(item, 'reconciliationAt', 'ReconciliationAt') ?? '',
    ),
    fileName: asString(pick(item, 'fileName', 'FileName')),
    createdAt: String(pick(item, 'createdAt', 'CreatedAt') ?? ''),
    createdBy: asString(pick(item, 'createdBy', 'CreatedBy')),
    status: asRunStatus(pick(item, 'status', 'Status')),
    totalCount: asNumber(pick(item, 'totalCount', 'TotalCount')),
    completedCount: asNumber(pick(item, 'completedCount', 'CompletedCount')),
    message: asString(pick(item, 'message', 'Message')),
  };
};

const asRunStatus = (
  value: unknown,
): StockReconciliationRunStatus | undefined => {
  const status = String(value ?? '').trim();
  if (
    status === 'Uploading' ||
    status === 'Pending' ||
    status === 'Processing' ||
    status === 'Completed' ||
    status === 'Failed'
  ) {
    return status;
  }
  return undefined;
};

export const unwrapStockReconciliationHistory = (
  value: unknown,
): StockReconciliationHistory => {
  const payload = unwrapPayload(value) ?? {};
  const itemsValue = pick<unknown>(payload, 'items', 'Items');
  return {
    items: Array.isArray(itemsValue)
      ? itemsValue.map(normalizeHistoryItem)
      : [],
    total: asNumber(pick(payload, 'total', 'Total')),
  };
};

export const unwrapStockReconciliationQueue = (
  value: unknown,
): StockReconciliationQueue => {
  const payload = unwrapPayload(value) ?? {};
  const itemsValue = pick<unknown>(payload, 'items', 'Items');
  return {
    items: Array.isArray(itemsValue)
      ? itemsValue.map(normalizeHistoryItem)
      : [],
  };
};

const asStringList = (value: unknown) =>
  Array.isArray(value)
    ? value.map((item) => String(item ?? '')).filter(Boolean)
    : [];

export const unwrapStockReconciliationRows = (
  value: unknown,
): StockReconciliationRowPage => {
  const payload = unwrapPayload(value) ?? {};
  const itemsValue = pick<unknown>(payload, 'items', 'Items');
  return {
    items: Array.isArray(itemsValue) ? itemsValue.map(normalizeRow) : [],
    total: asNumber(pick(payload, 'total', 'Total')),
    statuses: asStringList(pick(payload, 'statuses', 'Statuses')),
    locations: asStringList(pick(payload, 'locations', 'Locations')),
  };
};

export const unwrapStockReconciliationQueueItem = (
  value: unknown,
): StockReconciliationHistoryItem | undefined => {
  const payload = unwrapPayload(value);
  if (!payload) return undefined;
  const item = normalizeHistoryItem(payload);
  return item.id || item.status ? item : undefined;
};
